import type { UseQueryOptions } from "@pinia/colada";

import { objectToCamel } from "ts-case-convert";
import { toValue } from "vue";

import type { HomepageResource } from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";

import { toSearchParams } from "@solvari/utils";

import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { cmsCollectionEntriesUrl } from "~/shared/composables/useCmsApi.ts";
import { useStatamicPreview } from "~/shared/composables/useStatamicPreview.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

function useHomepagePage(params: { locale: LocaleMorpheus }) {
  const cmsUrl = cmsCollectionEntriesUrl("pages");

  const { previewToken, previewSite } = useStatamicPreview();

  return {
    key: () => ["cms", "homepage", params.locale, toValue(previewToken)],
    query: handleNuxtQuery(async () => {
      const data = objectToCamel(
        await kyCacheTags
          .get(cmsUrl, {
            searchParams: toSearchParams(
              {
                filter: {
                  site: previewSite.value ?? params.locale,
                  blueprint: "homepage",
                },
                limit: 1,
                token: previewToken.value,
              },
              { nestedKeys: true },
            ),
          })
          .json<HomepageResource>(),
      );
      return data.data[0] ?? null;
    }),
    staleTime: 5 * 60_000,
  } satisfies UseQueryOptions;
}

export { useHomepagePage };
